import { jsx } from "react/jsx-runtime";
import { isEditor } from "../../utils/isEditor.esm.js";
import { FieldConfig, DISCOUNT_POINTS } from "./ClosingCostCalculator.utils.esm.js";
import ClosingCostCalculator from "./ClosingCostCalculator.esm.js";
const ClosingCostCalculatorBlock = {
  Component: ({
    attrs
  }) => {
    const {
      homePrice,
      downPayment,
      interestRate,
      purchasingAnyDiscountPoints,
      downPaymentPercent,
      discountPoints,
      creditReportFee,
      titleSearchFee,
      attorneyFees,
      appraisalFee,
      homeInspectionFee,
      pestInspectionFee,
      surveyFee,
      recordingFee,
      courierFee,
      notaryFee,
      hoaTransferFee,
      floodCertificationFee,
      bankProcessingFee,
      propertyTaxes,
      loanOriginationFee,
      titleInsurance,
      settelementOrEscrowFee,
      homeownerInsurance,
      prepaidInterest
    } = attrs;
    const defaultValues = {
      homePrice,
      downPayment,
      downPaymentPercent,
      interestRate,
      purchasingAnyDiscountPoints,
      discountPoints,
      creditReportFee,
      titleSearchFee,
      attorneyFees,
      appraisalFee,
      homeInspectionFee,
      pestInspectionFee,
      surveyFee,
      recordingFee,
      courierFee,
      notaryFee,
      hoaTransferFee,
      floodCertificationFee,
      bankProcessingFee,
      propertyTaxes,
      loanOriginationFee,
      titleInsurance,
      settelementOrEscrowFee,
      homeownerInsurance,
      prepaidInterest
    };
    return /* @__PURE__ */ jsx(ClosingCostCalculator, { ...{
      defaultValues
    } }, isEditor() ? JSON.stringify(defaultValues) : void 0);
  },
  metadata: {
    version: "1.0",
    name: "constellation/closing-cost-calculator",
    title: "Closing Cost Calculator",
    description: "Estimate the closing costs for buying a home.",
    icon: "calculator",
    keywords: ["mortgages", "calculators", "closing", "cost"],
    supports: {
      customClassName: false,
      html: false
    },
    category: "Calculators",
    attributes: {
      [FieldConfig.homePrice.name]: {
        type: "number",
        default: 4e5,
        label: "Default home price",
        control: "number"
      },
      [FieldConfig.downPayment.name]: {
        type: "number",
        default: 8e4,
        label: "Default downpayment",
        control: "number"
      },
      [FieldConfig.interestRate.name]: {
        type: "number",
        label: "Default interest rate",
        default: 4.176,
        control: "number"
      },
      [FieldConfig.downPaymentPercent.name]: {
        type: "number",
        label: "Default downpayment percent",
        default: 20,
        control: "number"
      },
      [FieldConfig.purchasingAnyDiscountPoints.name]: {
        type: "string",
        default: DISCOUNT_POINTS.defaultPoint,
        label: "Default purchasing discount points",
        control: "select",
        choices: FieldConfig.purchasingAnyDiscountPoints.options
      },
      [FieldConfig.loanOriginationFee.name]: {
        type: "number",
        default: 1600,
        label: "Loan fees: Default loan origination fee",
        control: "number"
      },
      [FieldConfig.discountPoints.name]: {
        type: "number",
        default: 0,
        label: "Loan fees: Default discount points",
        control: "number"
      },
      [FieldConfig.creditReportFee.name]: {
        type: "number",
        default: 35,
        label: "Loan fees: Default credit report fee",
        control: "number"
      },
      [FieldConfig.titleSearchFee.name]: {
        type: "number",
        default: 2e3,
        label: "Title and legal fees: Default title search fee",
        control: "number"
      },
      [FieldConfig.attorneyFees.name]: {
        type: "number",
        default: 500,
        label: "Title and legal fees: Default attorney fee",
        control: "number"
      },
      [FieldConfig.titleInsurance.name]: {
        type: "number",
        default: 1e3,
        label: "Title and legal fees: Default title insurance fee",
        control: "number"
      },
      [FieldConfig.settelementOrEscrowFee.name]: {
        type: "number",
        default: 1e3,
        label: "Title and legal fees: Default settelement or escrow fee",
        control: "number"
      },
      [FieldConfig.homeownerInsurance.name]: {
        type: "number",
        default: 1400,
        label: "Prepaid expenses: Default homeowner insurance fee",
        control: "number"
      },
      [FieldConfig.propertyTaxes.name]: {
        type: "number",
        default: 315,
        label: "Prepaid expenses: Default property taxes fee",
        control: "number"
      },
      [FieldConfig.prepaidInterest.name]: {
        type: "number",
        default: 789,
        label: "Prepaid expenses: Default prepaid interest fee",
        control: "number"
      },
      [FieldConfig.appraisalFee.name]: {
        type: "number",
        default: 500,
        label: "Inspection and appraisal fees: Default appraisal fee",
        control: "number"
      },
      [FieldConfig.homeInspectionFee.name]: {
        type: "number",
        default: 400,
        label: "Inspection and appraisal fees: Default home inspection fee",
        control: "number"
      },
      [FieldConfig.pestInspectionFee.name]: {
        type: "number",
        default: 100,
        label: "Inspection and appraisal fees: Default pest inspection fee",
        control: "number"
      },
      [FieldConfig.surveyFee.name]: {
        type: "number",
        default: 500,
        label: "Inspection and appraisal fees: Default survey fee",
        control: "number"
      },
      [FieldConfig.recordingFee.name]: {
        type: "number",
        default: 150,
        label: "Government fees: Default recording fee",
        control: "number"
      },
      [FieldConfig.courierFee.name]: {
        type: "number",
        default: 20,
        label: "Additional fees: Default courier fee",
        control: "number"
      },
      [FieldConfig.notaryFee.name]: {
        type: "number",
        default: 100,
        label: "Additional fees: Default notary fee",
        control: "number"
      },
      [FieldConfig.hoaTransferFee.name]: {
        type: "number",
        default: 0,
        label: "Additional fees: Default HOA transfer fee",
        control: "number"
      },
      [FieldConfig.floodCertificationFee.name]: {
        type: "number",
        default: 15,
        label: "Additional fees: Default flood certification fee",
        control: "number"
      },
      [FieldConfig.bankProcessingFee.name]: {
        type: "number",
        default: 50,
        label: "Additional fees: Default bank processing fee",
        control: "number"
      }
    }
  }
};
export {
  ClosingCostCalculatorBlock as default
};
